import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Popover, Button, Stack, Tooltip } from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import AddToVocabButton from './AddToVocabButton';
import { layoutSentence, vocabKey } from '../utils/wordMatching';

/**
 * Renders a story from its word matching (annotations): every word/phrase is
 * tappable and shows its meaning in context; each sentence has a small
 * translate button that reveals its English right under it.
 *
 * Props:
 *   annotations   { paragraphs: [[{ text, en, w: [{ t, g, l? }] }]] }
 *   vocabulary    learner's saved words (colours known/learning words)
 *   languageCode  target language (for Add to vocab)
 *   onMoreWord    optional ({ word, translation, context }) => void — opens the
 *                 full word dialog (knowledge level, tags…)
 *   typographySx  styles for the paragraphs (font, size, line height)
 */
const LEVEL_COLORS = { 1: 'error.main', 2: 'warning.main', 3: 'success.main' };

export default function AnnotatedStoryText({ annotations, vocabulary = [], languageCode, onMoreWord, typographySx }) {
  const [openEnglish, setOpenEnglish] = useState({});
  const [popover, setPopover] = useState(null); // { anchorEl, unit, sentence }

  const vocabLevels = useMemo(() => {
    const map = new Map();
    (vocabulary || []).forEach((v) => { if (v?.word) map.set(vocabKey(v.word), v.knowledge_level); });
    return map;
  }, [vocabulary]);

  const toggleEnglish = (key) => setOpenEnglish((prev) => ({ ...prev, [key]: !prev[key] }));
  const openUnit = (event, unit, sentence) => setPopover({ anchorEl: event.currentTarget, unit, sentence });

  const paragraphs = annotations?.paragraphs || [];

  return (
    <Box>
      {paragraphs.map((para, p) => (
        <Typography key={p} component="p" variant="body1" sx={{ mb: 2.5, ...typographySx }}>
          {para.map((sentence, s) => {
            const key = `${p}-${s}`;
            const pieces = layoutSentence(sentence.text, sentence.w);
            return (
              <React.Fragment key={key}>
                {pieces.map((piece, i) => {
                  if (piece.type === 'gap') return <span key={i}>{piece.text}</span>;
                  const { unit } = piece;
                  const level = vocabLevels.get(vocabKey(unit.l)) ?? vocabLevels.get(vocabKey(unit.t));
                  const isPhrase = /\s/.test(unit.t);
                  return (
                    <Box
                      key={i}
                      component="span"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => openUnit(e, unit, sentence)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openUnit(e, unit, sentence); } }}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 0.5,
                        color: LEVEL_COLORS[level] || 'inherit',
                        textDecorationLine: isPhrase ? 'underline' : 'none',
                        textDecorationStyle: 'dotted',
                        textDecorationColor: (t) => t.palette.text.disabled,
                        textUnderlineOffset: '5px',
                        '&:hover, &:focus-visible': { bgcolor: 'action.hover', outline: 'none' },
                      }}
                    >
                      {unit.t}
                    </Box>
                  );
                })}
                {sentence.en && (
                  <Tooltip title={openEnglish[key] ? 'Hide English' : 'Show English'}>
                    <IconButton
                      size="small"
                      aria-label={openEnglish[key] ? 'Hide English' : 'Show English'}
                      onClick={() => toggleEnglish(key)}
                      sx={{
                        p: 0.25, mx: 0.25, verticalAlign: 'baseline',
                        color: openEnglish[key] ? 'primary.main' : 'text.disabled',
                      }}
                    >
                      <TranslateIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
                {openEnglish[key] && (
                  <Box component="span" sx={{
                    display: 'block', my: 0.75, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider',
                    fontStyle: 'italic', color: 'text.secondary', fontSize: '0.88em', lineHeight: 1.6,
                    fontFamily: 'inherit',
                  }}>
                    {sentence.en}
                  </Box>
                )}
                {s < para.length - 1 ? ' ' : ''}
              </React.Fragment>
            );
          })}
        </Typography>
      ))}

      <Popover
        open={Boolean(popover)}
        anchorEl={popover?.anchorEl}
        onClose={() => setPopover(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 2, maxWidth: 300, borderRadius: 2 } }}
        disableRestoreFocus
      >
        {popover && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{popover.unit.t}</Typography>
            {popover.unit.l && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Dictionary form: {popover.unit.l}
              </Typography>
            )}
            <Typography variant="body1" sx={{ mt: 1 }}>
              {popover.unit.g || <em>No meaning yet</em>}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <AddToVocabButton
                word={popover.unit.l || popover.unit.t}
                translation={popover.unit.g}
                context={popover.sentence.text}
                languageCode={languageCode}
              />
              {onMoreWord && (
                <Button
                  size="small"
                  onClick={() => {
                    onMoreWord({ word: popover.unit.l || popover.unit.t, translation: popover.unit.g, context: popover.sentence.text });
                    setPopover(null);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  More…
                </Button>
              )}
            </Stack>
          </>
        )}
      </Popover>
    </Box>
  );
}
